import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  Copy,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  SendHorizontal,
  Upload,
  X,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Avatar as UserAvatar } from '@/components/design-system/Avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import {
  AGENT_CHAT_ACCEPTED_ATTACHMENT_TYPES,
  classifyAgentChatFile,
  formatAttachmentSize,
  isPreviewableAgentChatFile,
} from '@/frontend/pages/Dashboard/AgentChats/attachments'
import type {
  AgentChatAttachment,
  AgentChatDigitalWorker,
  AgentChatSessionDetail,
} from '@/frontend/pages/Dashboard/AgentChats/types'

interface ConversationPanelProps {
  // Embedded chat surfaces own their height and need this panel to shrink
  // inside that boundary instead of calculating another viewport height.
  fillContainer?: boolean
  draft: string
  error: string | null
  attachmentError: string | null
  attachments: File[]
  isSending: boolean
  isUploadingAttachments: boolean
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'rejected'
  digitalWorker: AgentChatDigitalWorker
  session: AgentChatSessionDetail | null
  currentUserName?: string | null
  onDraftChange: (value: string) => void
  onAddAttachments: (files: File[]) => void
  onRemoveAttachment: (index: number) => void
  onSendMessage: () => void
}

const LONG_RUNNING_WAIT_MS = 45_000

function formatMessageTime(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function usePendingResponseLabel(
  session: AgentChatSessionDetail | null,
  responsePending: boolean,
  workerName: string,
): string {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!responsePending) return undefined

    const intervalId = window.setInterval(() => setNow(Date.now()), 5000)
    return () => window.clearInterval(intervalId)
  }, [responsePending])

  const startedAt = session?.pending_turn?.submitted_at || session?.pending_turn?.created_at || null
  const startedTime = startedAt ? new Date(startedAt).getTime() : NaN
  const longRunning = responsePending &&
    Number.isFinite(startedTime) &&
    now - startedTime >= LONG_RUNNING_WAIT_MS

  if (longRunning) return 'O agente ainda está trabalhando...'

  return `${workerName} está digitando...`
}

function MessageActions({ content }: { content: string }) {
  const [copied, setCopied] = useState(false)
  const resetCopiedTimeoutRef = useRef<number | null>(null)

  useEffect(() => () => {
    if (resetCopiedTimeoutRef.current) window.clearTimeout(resetCopiedTimeoutRef.current)
  }, [])

  const handleCopy = () => {
    const writeClipboard = navigator.clipboard?.writeText(content)
    if (!writeClipboard) return

    void writeClipboard.then(() => {
      setCopied(true)
      if (resetCopiedTimeoutRef.current) window.clearTimeout(resetCopiedTimeoutRef.current)
      resetCopiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={copied ? 'Mensagem copiada' : 'Ações da mensagem'}
          className="flex size-6 items-center justify-center rounded bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          {copied ? (
            <Check aria-hidden="true" className="size-3 text-green-500" />
          ) : (
            <Copy aria-hidden="true" className="size-3" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-32 rounded-lg p-1 shadow-xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="w-full justify-start gap-2 rounded px-2 py-1 text-xs"
        >
          {copied ? (
            <Check aria-hidden="true" className="size-3 text-green-500" />
          ) : (
            <Copy aria-hidden="true" className="size-3" />
          )}
          {copied ? 'Copiado!' : 'Copiar'}
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MessageAttachments({
  attachments,
  isMe,
}: {
  attachments: AgentChatAttachment[]
  isMe: boolean
}) {
  if (attachments.length === 0) return null

  return (
    <div className="mt-2 grid gap-2">
      {attachments.map((attachment) => {
        const previewUrl = attachment.previewable ? (attachment.preview_url || attachment.url) : null
        const content = previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt={attachment.filename}
              loading="lazy"
              className="max-h-56 w-full rounded object-contain"
            />
            <AttachmentCaption attachment={attachment} isMe={isMe} icon="image" />
          </>
        ) : (
          <AttachmentCaption attachment={attachment} isMe={isMe} icon="file" />
        )

        return (
          <a
            key={attachment.id}
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              'block min-w-0 overflow-hidden rounded-md border text-left transition hover:opacity-90',
              isMe
                ? 'border-primary-foreground/25 bg-primary-foreground/10 text-primary-foreground'
                : 'border-border bg-background text-foreground',
            )}
          >
            {content}
          </a>
        )
      })}
    </div>
  )
}

function AttachmentCaption({
  attachment,
  isMe,
  icon,
}: {
  attachment: AgentChatAttachment
  isMe: boolean
  icon: 'image' | 'file'
}) {
  const Icon = icon === 'image' ? ImageIcon : FileText

  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-2 px-2 py-1.5 text-xs',
        isMe ? 'text-primary-foreground/90' : 'text-muted-foreground',
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
      <span className="shrink-0">{formatAttachmentSize(attachment.byte_size)}</span>
    </span>
  )
}

interface SelectedAttachmentView {
  file: File
  kind: 'image' | 'file'
  previewUrl: string | null
}

function SelectedAttachmentPreview({
  view,
  index,
  onRemove,
}: {
  view: SelectedAttachmentView
  index: number
  onRemove: (index: number) => void
}) {
  const Icon = view.kind === 'image' ? ImageIcon : FileText

  return (
    <div className="flex max-w-full items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs shadow-sm sm:max-w-64">
      {view.previewUrl ? (
        <img
          src={view.previewUrl}
          alt=""
          className="size-8 shrink-0 rounded object-cover"
        />
      ) : (
        <span className="flex size-8 shrink-0 items-center justify-center rounded bg-accent text-muted-foreground">
          <Icon aria-hidden="true" className="size-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-foreground">{view.file.name}</span>
        <span className="block text-muted-foreground">{formatAttachmentSize(view.file.size)}</span>
      </span>
      <button
        type="button"
        onClick={() => onRemove(index)}
        aria-label={`Remover ${view.file.name}`}
        className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X aria-hidden="true" className="size-3.5" />
      </button>
    </div>
  )
}

function filesFromDataTransfer(dataTransfer: DataTransfer): File[] {
  const files = Array.from(dataTransfer.files || [])
  if (files.length > 0) return files

  return Array.from(dataTransfer.items || [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null)
}

function hasTransferredFiles(dataTransfer: DataTransfer): boolean {
  return (
    Array.from(dataTransfer.types || []).includes('Files') ||
    dataTransfer.files.length > 0 ||
    Array.from(dataTransfer.items || []).some((item) => item.kind === 'file')
  )
}

export function ConversationPanel({
  fillContainer = false,
  draft,
  error,
  attachmentError,
  attachments,
  isSending,
  isUploadingAttachments,
  connectionStatus,
  digitalWorker,
  session,
  currentUserName,
  onDraftChange,
  onAddAttachments,
  onRemoveAttachment,
  onSendMessage,
}: ConversationPanelProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dragDepthRef = useRef(0)
  const [dropActive, setDropActive] = useState(false)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [session?.messages.length, session?.awaiting_response, isSending])

  const workerName = session?.digital_worker_name || digitalWorker.name
  const workerAvatarUrl = session?.digital_worker_avatar_url || digitalWorker.avatar_url
  const workerInitial = workerName.trim().charAt(0).toUpperCase() || 'A'
  // Seed of the stable identity hue (B.4), resolved like the name/avatar above.
  const workerHueStyle = agentHueStyle(session?.digital_worker_id || digitalWorker.id)
  const trimmedUserName = currentUserName?.trim() || ''
  const messages = session?.messages || []
  const canSend = session?.can_send ?? digitalWorker.can_send
  const awaitingResponse = session?.awaiting_response ?? false
  const selectedAttachmentViews = useMemo<SelectedAttachmentView[]>(() => (
    attachments.map((file) => {
      const classification = classifyAgentChatFile(file)
      const canPreview = typeof URL !== 'undefined' && isPreviewableAgentChatFile(file)
      return {
        file,
        kind: classification?.kind || 'file',
        previewUrl: canPreview ? URL.createObjectURL(file) : null,
      }
    })
  ), [attachments])

  useEffect(() => () => {
    selectedAttachmentViews.forEach((view) => {
      if (view.previewUrl) URL.revokeObjectURL(view.previewUrl)
    })
  }, [selectedAttachmentViews])

  const responsePending = awaitingResponse || (isSending && !isUploadingAttachments)
  const responsePendingLabel = usePendingResponseLabel(session, responsePending, workerName)
  // The composer doesn't need cable to send: handleSendMessage falls back to
  // HTTP when the cable isn't connected. Disable only on real submit blockers.
  // Active runs accept steering (and the controller maps `/stop` to the abort
  // endpoint), so only transport availability and an in-flight submission lock
  // the legacy composer.
  const disableInput = !canSend || isSending
  const inputPlaceholder = !canSend
    ? 'O gateway deste agente não está disponível no momento.'
    : `Mande uma mensagem para ${workerName}…`

  const resetDropState = useCallback(() => {
    dragDepthRef.current = 0
    setDropActive(false)
  }, [])

  useEffect(() => {
    if (disableInput) resetDropState()
  }, [disableInput, resetDropState])

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!hasTransferredFiles(event.dataTransfer)) return

    event.preventDefault()
    dragDepthRef.current += 1
    if (!disableInput) setDropActive(true)
  }, [disableInput])

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!hasTransferredFiles(event.dataTransfer)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = disableInput ? 'none' : 'copy'
    if (!disableInput) setDropActive(true)
  }, [disableInput])

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!hasTransferredFiles(event.dataTransfer) && dragDepthRef.current === 0) return

    event.preventDefault()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDropActive(false)
  }, [])

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!hasTransferredFiles(event.dataTransfer)) return

    event.preventDefault()
    const droppedFiles = filesFromDataTransfer(event.dataTransfer)
    resetDropState()
    if (disableInput || droppedFiles.length === 0) return

    onAddAttachments(droppedFiles)
  }, [disableInput, onAddAttachments, resetDropState])

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedFiles = filesFromDataTransfer(event.clipboardData)
    if (pastedFiles.length === 0) return

    event.preventDefault()
    if (!disableInput) onAddAttachments(pastedFiles)
  }, [disableInput, onAddAttachments])

  return (
    <div className={cn('flex w-full flex-col gap-3', fillContainer && 'h-full min-h-0')}>
      <Card
        // Standalone surfaces use dvh so the mobile keyboard cannot cover the
        // composer; embedded surfaces shrink inside their owning container.
        className={cn(
          '@container/chat relative flex min-h-0 flex-col gap-0 overflow-hidden py-0 shadow-none',
          fillContainer ? 'flex-1' : 'h-[min(75dvh,calc(100dvh-10rem))]',
          dropActive && 'ring-2 ring-brand/40',
        )}
        data-testid="agent-chat-conversation"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {dropActive && (
          <div
            className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-background/80 backdrop-blur-sm"
            aria-hidden="true"
          >
            <div className="flex items-center gap-2 rounded-md border border-brand bg-card px-3 py-2 text-sm font-medium text-foreground shadow-lg">
              <Upload aria-hidden="true" className="size-4 text-brand" />
              Solte os arquivos para anexar
            </div>
          </div>
        )}

        <CardContent className="min-h-0 flex-1 p-0">
          <ScrollArea
            aria-label="Conversa com o colaborador"
            role="log"
            className="h-full bg-background"
          >
            <div className="flex flex-col gap-1.5 p-4" data-testid="agent-chat-thread">
              {messages.map((message) => {
                const isMe = message.role === 'user'
                const hasContent = message.content.trim().length > 0
                return (
                  <div
                    key={message.id}
                    data-testid={`agent-chat-message-${message.id}`}
                    className={cn('group flex gap-2', isMe ? 'justify-end' : 'justify-start')}
                  >
                    <div
                      className={cn(
                        'flex max-w-[80%] items-start gap-2',
                        isMe && 'flex-row-reverse',
                      )}
                    >
                      {!isMe ? (
                        <Avatar className="size-8 shrink-0">
                          {workerAvatarUrl ? <AvatarImage alt="" src={workerAvatarUrl} /> : null}
                          <AvatarFallback className="bg-agent text-agent-foreground" style={workerHueStyle}>
                            {workerInitial}
                          </AvatarFallback>
                        </Avatar>
                      ) : (message.user_name?.trim() || trimmedUserName) ? (
                        <UserAvatar
                          name={message.user_name?.trim() || trimmedUserName}
                          size="sm"
                          className="shrink-0"
                        />
                      ) : null}
                      <div className={cn('flex flex-col', isMe && 'items-end')}>
                        <div
                          className={cn(
                            'rounded-md px-3 py-2 text-sm leading-relaxed',
                            isMe
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-accent text-foreground',
                          )}
                        >
                          {hasContent && isMe ? (
                            <div className="whitespace-pre-wrap break-words">{message.content}</div>
                          ) : hasContent ? (
                            <SimpleMarkdown
                              content={message.content}
                              allowImages={false}
                              className="break-words [&_*:first-child]:mt-0 [&_*:last-child]:mb-0"
                            />
                          ) : null}
                          <MessageAttachments attachments={message.attachments || []} isMe={isMe} />
                        </div>
                        <div className="mt-0.5 flex items-center gap-2">
                          <time
                            className="text-[10px] text-muted-foreground"
                            dateTime={message.created_at}
                          >
                            {formatMessageTime(message.created_at)}
                          </time>
                          <div className="opacity-0 transition-opacity group-hover:opacity-100">
                            <MessageActions content={message.content} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
              {responsePending && (
                <div className="flex justify-start gap-2" aria-live="polite">
                  <div className="flex max-w-[80%] items-start gap-2">
                    <Avatar className="size-8 shrink-0">
                      {workerAvatarUrl ? <AvatarImage alt="" src={workerAvatarUrl} /> : null}
                      <AvatarFallback className="bg-agent text-agent-foreground" style={workerHueStyle}>
                        {workerInitial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="rounded-md bg-accent px-3 py-2 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {responsePendingLabel}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {messages.length === 0 && !responsePending && (
                <div
                  data-testid="agent-chat-empty-thread"
                  className="rounded-2xl border border-dashed px-4 py-10 text-center text-sm text-muted-foreground"
                >
                  Diga olá para {workerName} para começar a conversa.
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>
        </CardContent>

        <div className="shrink-0 p-2">
          {selectedAttachmentViews.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2 px-1">
              {selectedAttachmentViews.map((view, index) => (
                <SelectedAttachmentPreview
                  key={`${view.file.name}-${view.file.size}-${index}`}
                  view={view}
                  index={index}
                  onRemove={onRemoveAttachment}
                />
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={AGENT_CHAT_ACCEPTED_ATTACHMENT_TYPES}
            disabled={disableInput}
            className="sr-only"
            onChange={(event) => {
              onAddAttachments(Array.from(event.currentTarget.files || []))
              event.currentTarget.value = ''
            }}
          />

          <div className="relative">
            <Textarea
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
              onPaste={handlePaste}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  onSendMessage()
                }
              }}
              disabled={disableInput}
              rows={2}
              placeholder={inputPlaceholder}
              aria-label={`Mensagem para ${workerName}`}
              className="max-h-40 min-h-[44px] resize-none overflow-y-auto rounded-2xl border-border bg-card py-3 pr-14 pl-14 shadow-sm focus-visible:ring-2 focus-visible:ring-brand/30"
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disableInput}
                  aria-label="Anexar arquivo"
                  className="absolute bottom-2 left-3 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <Paperclip aria-hidden="true" className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Anexar arquivo</TooltipContent>
            </Tooltip>
            <button
              type="button"
              onClick={onSendMessage}
              disabled={!draft.trim() || disableInput}
              aria-label="Enviar mensagem"
              className="absolute bottom-2 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-background transition-all hover:bg-foreground/90 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:scale-100"
            >
              {responsePending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SendHorizontal className="h-4 w-4" strokeWidth={2.5} />
              )}
            </button>
          </div>

          {attachmentError && (
            <p className="mt-1 px-2 text-xs text-destructive" role="alert">{attachmentError}</p>
          )}
          {isUploadingAttachments && (
            <p className="mt-1 px-2 text-xs text-muted-foreground" role="status">
              Enviando anexos…
            </p>
          )}
        </div>
      </Card>

      {/* 'connecting' is the normal Action Cable handshake; 'disconnected' is a
          recoverable drop; 'rejected' is a terminal refusal that needs a reload. */}
      {connectionStatus === 'disconnected' && (
        <p
          data-testid="agent-chat-connection-banner"
          className="text-center text-sm text-amber-700"
          role="status"
        >
          Conexão em tempo real indisponível. A página será sincronizada quando a conexão voltar.
        </p>
      )}

      {connectionStatus === 'rejected' && (
        <p
          data-testid="agent-chat-connection-banner"
          className="text-center text-sm text-destructive"
          role="alert"
        >
          Não foi possível conectar a este canal. Atualize a página para tentar de novo.
        </p>
      )}

      {error && (
        <p className="text-center text-sm text-destructive" role="alert">{error}</p>
      )}

    </div>
  )
}
